import React, { useState } from 'react';
import { Upload, X, Image, Video, Music, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const FileUpload = ({ sessionId, stepId = null, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    const uploadedFiles = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (stepId) formData.append('step_id', stepId);

        const response = await axios.post(
          `${API}/uploads/session/${sessionId}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`Upload progress: ${percentCompleted}%`);
            }
          }
        );

        uploadedFiles.push(response.data);
        toast.success(`${file.name} hochgeladen`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.detail || `Fehler beim Hochladen von ${file.name}`);
      }
    }

    setUploading(false);
    if (onUploadComplete && uploadedFiles.length > 0) {
      onUploadComplete(uploadedFiles);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  return (
    <div
      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
        dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-600 bg-gray-900'
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        type="file"
        id={`file-upload-${sessionId}-${stepId || 'session'}`}
        multiple
        accept="image/*,video/*,audio/*"
        onChange={handleChange}
        className="hidden"
        disabled={uploading}
      />

      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-gray-300">Wird hochgeladen...</p>
        </div>
      ) : (
        <label
          htmlFor={`file-upload-${sessionId}-${stepId || 'session'}`}
          className="cursor-pointer flex flex-col items-center gap-2"
        >
          <Upload className="w-8 h-8 text-gray-400" />
          <div>
            <p className="text-gray-300">
              <span className="text-blue-400 underline">Klicken Sie hier</span> oder ziehen Sie Dateien hierher
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Bilder, Videos oder Audio (max 100MB)
            </p>
          </div>
        </label>
      )}
    </div>
  );
};

export const FileAttachmentList = ({ attachments, onDelete }) => {
  const getIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="w-5 h-5" />;
      case 'video': return <Video className="w-5 h-5" />;
      case 'audio': return <Music className="w-5 h-5" />;
      default: return <Upload className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="space-y-2 mt-4">
      <p className="text-sm font-semibold text-gray-300">Anhänge:</p>
      {attachments.map((att) => (
        <div
          key={att.id}
          className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="text-blue-400">{getIcon(att.file_type)}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-200 truncate">{att.filename}</p>
              <p className="text-xs text-gray-500">{formatFileSize(att.file_size)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API}${att.url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              Öffnen
            </a>
            {onDelete && (
              <button
                onClick={() => onDelete(att.id)}
                className="text-red-400 hover:text-red-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
